/**
 * Which regions get the Hub look, and how to override that while testing.
 *
 * There is one build and one deploy for every region — the three Firebase hosting targets are
 * design variants, not regions, and all publish the same folder. So a theme cannot be rolled out
 * by deploying to one place.
 *
 * What each region does have is its own hostname, and the app already derives its region from
 * that. A hostname is a real boundary: staff on the US domain cannot see the New Zealand theme,
 * whatever they click. Gating on it gives a genuine per-region rollout from a single deploy,
 * and rolling back is redeploying this constant rather than reverting a design.
 */

export const HUB_THEME_ATTRIBUTE = "data-theme";
export const HUB_THEME_VALUE = "hub";

/**
 * Every region, now.
 *
 * <p>This was one region while the look was being judged. New Zealand ran it live long enough to
 * settle that, and a staged rollout past that point is nine portals drifting apart rather than a
 * safety measure — an office on the old palette was getting the renamed tabs and the new pages on
 * the old surfaces, which reads as a half-finished deploy rather than a deliberate hold.
 *
 * <p>The per-browser override below is what remains of the safety: `?theme=off` still turns it
 * off for whoever needs that, and setting this back to a specific set is still the rollback.
 */
export const HUB_THEME_EVERYWHERE = true;

/**
 * Regions running the Hub look while {@link HUB_THEME_EVERYWHERE} is off.
 *
 * Kept so the rollout can be narrowed again without rewriting the decision — flip the flag and
 * this is the list that applies.
 */
export const HUB_THEME_REGIONS: ReadonlySet<string> = new Set(["nz"]);

/**
 * A per-browser override, so the look can be checked on any region without switching anyone
 * over. `?theme=hub` turns it on and is remembered; `?theme=off` turns it off again.
 *
 * Deliberately opt-in per browser rather than a shared setting: someone trying a theme should
 * not be able to change what another office sees by doing so.
 */
const OVERRIDE_KEY = "pcg.admin.themeOverride";

type Override = "hub" | "off" | null;

const readStoredOverride = (): Override => {
  try {
    const stored = window.localStorage.getItem(OVERRIDE_KEY);
    return stored === "hub" || stored === "off" ? stored : null;
  } catch {
    // Private browsing or a locked-down profile: no override, not a crash.
    return null;
  }
};

const writeStoredOverride = (value: Override): void => {
  try {
    if (value === null) window.localStorage.removeItem(OVERRIDE_KEY);
    else window.localStorage.setItem(OVERRIDE_KEY, value);
  } catch {
    // The override lasts the page rather than the browser. Nothing worth failing over.
  }
};

/** Reads `?theme=` if present, remembers it, and returns whatever override is now in force. */
export const resolveOverride = (search: string): Override => {
  let param: string | null = null;
  try {
    param = new URLSearchParams(search).get("theme");
  } catch {
    param = null;
  }

  if (param === "hub" || param === "off") {
    writeStoredOverride(param);
    return param;
  }
  // "clear" removes the override and hands the decision back to the region.
  if (param === "clear") {
    writeStoredOverride(null);
    return null;
  }
  return readStoredOverride();
};

/**
 * Whether the Hub look is on.
 *
 * An explicit override wins, because the only reason to set one is to see something other than
 * what the region would give you.
 */
export const shouldUseHubTheme = (region: string | null, search: string): boolean => {
  const override = resolveOverride(search);
  if (override === "hub") return true;
  if (override === "off") return false;
  // Deliberately not conditioned on the region being known: an unrecognised hostname is a
  // portal too, and leaving it on the old palette is the drift this change exists to end.
  if (HUB_THEME_EVERYWHERE) return true;
  return region != null && HUB_THEME_REGIONS.has(region.toLowerCase());
};

/** Put the decision on <html>, where the palette variables are scoped. */
export const applyHubTheme = (enabled: boolean): void => {
  const root = document.documentElement;
  if (enabled) root.setAttribute(HUB_THEME_ATTRIBUTE, HUB_THEME_VALUE);
  else root.removeAttribute(HUB_THEME_ATTRIBUTE);
};


/* ------------------------------------------------------------------ *
 * Light or dark, remembered per person.
 * ------------------------------------------------------------------ */

export const MODE_ATTRIBUTE = "data-mode";

export type ThemeMode = "light" | "dark";

/**
 * Keyed by who is signed in, not by the browser.
 *
 * Offices share machines. A single key would mean the first person to pick light imposes it on
 * whoever sits down next, which is the opposite of a preference. Signed out, or before the user
 * has loaded, there is one shared key — nobody is being remembered wrongly at that point.
 */
const modeKey = (userId: number | string | null | undefined) =>
  userId == null || userId === "" ? "pcg.admin.mode" : `pcg.admin.mode.${userId}`;

export const readMode = (userId: number | string | null | undefined): ThemeMode | null => {
  try {
    const stored = window.localStorage.getItem(modeKey(userId));
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
};

export const writeMode = (
  userId: number | string | null | undefined,
  mode: ThemeMode
): void => {
  try {
    window.localStorage.setItem(modeKey(userId), mode);
  } catch {
    // A locked-down profile. The choice lasts the session rather than the machine.
  }
};

/**
 * What to show before anybody has chosen.
 *
 * The operating system's own setting, because somebody who has set their machine to light has
 * already answered this question once and should not have to answer it again. Dark when the
 * machine has no opinion, which is what this portal has always been.
 */
export const preferredMode = (): ThemeMode => {
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  } catch {
    return "dark";
  }
};

/**
 * What to show, in order of how much it is worth trusting.
 *
 * The account first, because that is the choice the person made deliberately and it should
 * follow them to any machine. Then this browser, which covers the moment between the page
 * painting and the profile arriving, and covers a signed-out or offline session. Then the
 * operating system, which is a better guess than an opinion of ours.
 */
export const resolveMode = (
  userId: number | string | null | undefined,
  accountPreference?: string | null
): ThemeMode => {
  if (accountPreference === "light" || accountPreference === "dark") return accountPreference;
  return readMode(userId) ?? preferredMode();
};

/**
 * Put the mode on <html> beside the theme.
 *
 * Dark removes the attribute rather than setting it, so the dark palette stays the one that
 * applies when nothing is stamped at all — including the first paint, before any of this runs.
 */
export const applyMode = (mode: ThemeMode): void => {
  const root = document.documentElement;
  if (mode === "light") root.setAttribute(MODE_ATTRIBUTE, "light");
  else root.removeAttribute(MODE_ATTRIBUTE);
};
